import React, { useState, useEffect, useMemo } from "react";
import { format, addWeeks, addDays, differenceInWeeks, differenceInDays, isWithinInterval, parseISO, startOfDay, endOfDay } from "date-fns";
import { dateStringToUTC, formatDateForInput, formatDateDisplay, addWeeksUTC, addDaysUTC, differenceInWeeksUTC, compareDatesUTC } from "@/lib/dateUtils";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar, Plus, Target, Trash2, Edit, Check, Clock, Dumbbell, Flame, Heart, Zap, Activity, Shield, Scale, TrendingDown, Archive, Moon, RotateCcw, Trophy, PartyPopper, Star, Eye } from "lucide-react";
import { cn } from "@/lib/utils";
import { Link, useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { isOnline, queueTrainingBlock, queueTrainingBlockUpdate, queueTrainingBlockDelete } from "@/lib/offlineSync";
import { useToast } from "@/hooks/use-toast";
import { useAuth, hasActiveAccess } from "@/hooks/useAuth";
import { Program } from "@/lib/mock-data";
import { useApiPrograms } from "@/hooks/useApiPrograms";
import { TRAINING_BLOCK_CATEGORIES, TrainingBlockCategory, DeloadSettings } from "@shared/schema";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { motion, AnimatePresence } from "framer-motion";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronUp } from "lucide-react";

export const CATEGORY_ICONS: Record<TrainingBlockCategory, React.ReactNode> = {
  general: <Dumbbell className="w-4 h-4" />,
  hypertrophy: <Flame className="w-4 h-4" />,
  strength: <Zap className="w-4 h-4" />,
  conditioning: <Activity className="w-4 h-4" />,
  athletic: <Target className="w-4 h-4" />,
  longevity: <Heart className="w-4 h-4" />,
  rest_recovery: <Moon className="w-4 h-4" />,
  weight_loss: <TrendingDown className="w-4 h-4" />,
  maintenance: <Scale className="w-4 h-4" />,
  technique: <Eye className="w-4 h-4" />,
  other: <Target className="w-4 h-4" />,
};

export const CATEGORY_COLORS: Record<TrainingBlockCategory, string> = {
  general: "bg-sky-500/20 text-sky-700 dark:text-sky-300 border-sky-500/40",
  hypertrophy: "bg-orange-500/20 text-orange-700 dark:text-orange-300 border-orange-500/40",
  strength: "bg-violet-500/20 text-violet-700 dark:text-violet-300 border-violet-500/40",
  conditioning: "bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-500/40",
  athletic: "bg-cyan-500/20 text-cyan-700 dark:text-cyan-300 border-cyan-500/40",
  longevity: "bg-rose-500/20 text-rose-700 dark:text-rose-300 border-rose-500/40",
  rest_recovery: "bg-teal-500/20 text-teal-700 dark:text-teal-300 border-teal-500/40",
  weight_loss: "bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500/40",
  maintenance: "bg-slate-500/20 text-slate-700 dark:text-slate-300 border-slate-500/40",
  technique: "bg-fuchsia-500/20 text-fuchsia-700 dark:text-fuchsia-300 border-fuchsia-500/40",
  other: "bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 border-indigo-500/40",
};

interface TrainingBlock {
  id: string;
  name: string;
  category: TrainingBlockCategory;
  startDate: string;
  endDate: string;
  status: string;
  programIds: string[] | null;
  deloadSettings: DeloadSettings | null;
  notes: string | null;
}

const DEFAULT_DELOAD_SETTINGS: DeloadSettings = {
  enabled: false,
  deloadWeeks: [],
  weightReduction: 40,
  repsReduction: 0,
  setsReduction: 50,
};

const DELOAD_PRESETS = [
  { id: 'light', label: 'Light', description: '20% less weight', weight: 20, reps: 0, sets: 0 },
  { id: 'moderate', label: 'Moderate', description: '40% less weight, half sets', weight: 40, reps: 0, sets: 50 },
  { id: 'full', label: 'Full', description: '50% less weight & sets', weight: 50, reps: 20, sets: 50 },
  { id: 'custom', label: 'Custom', description: 'Set your own', weight: 40, reps: 0, sets: 0 },
] as const;

type DeloadPresetId = typeof DELOAD_PRESETS[number]['id'];

// Helper to calculate current week number in a training block (1-indexed)
// Uses UTC date handling for consistency across timezones
export function getCurrentWeekInBlock(block: { startDate: string; endDate: string }): number {
  const startDate = new Date(block.startDate);
  const now = new Date();
  // Compare using UTC dates to avoid timezone issues
  const todayUTC = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const startUTC = Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), startDate.getUTCDate());
  const daysPassed = Math.max(0, Math.floor((todayUTC - startUTC) / (24 * 60 * 60 * 1000)));
  return Math.floor(daysPassed / 7) + 1; // 1-indexed week
}

// Check if current week is a deload week for a training block
export function isDeloadWeek(block: { startDate: string; endDate: string; deloadSettings: DeloadSettings | null }): boolean {
  if (!block.deloadSettings?.enabled || !block.deloadSettings.deloadWeeks.length) {
    return false;
  }
  const currentWeek = getCurrentWeekInBlock(block);
  return block.deloadSettings.deloadWeeks.includes(currentWeek);
}

// Get deload settings if currently in a deload week
export function getDeloadSettings(block: { startDate: string; endDate: string; deloadSettings: DeloadSettings | null }): DeloadSettings | null {
  if (!isDeloadWeek(block)) {
    return null;
  }
  return block.deloadSettings;
}

// Check if a specific date falls within a deload week of a training block
export function isDateInDeloadWeek(block: { startDate: string; endDate: string; deloadSettings: DeloadSettings | null }, date: Date): boolean {
  if (!block.deloadSettings?.enabled || !block.deloadSettings.deloadWeeks.length) {
    return false;
  }
  const startDate = new Date(block.startDate);
  const dateUTC = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  const startUTC = Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), startDate.getUTCDate());
  const daysPassed = Math.max(0, Math.floor((dateUTC - startUTC) / (24 * 60 * 60 * 1000)));
  const weekNumber = Math.floor(daysPassed / 7) + 1;
  return block.deloadSettings.deloadWeeks.includes(weekNumber);
}

// Round weight to nearest increment (e.g., 2.5 lbs/kg)
// Uses standard rounding: 44 → 45, 43.5 → 42.5, 46.25 → 47.5
export function roundToWeightIncrement(weight: number, increment: number = 2.5): number {
  return Math.round(weight / increment) * increment;
}

type CustomProgram = import("@/types/program").CustomProgram;

const TRAINING_BLOCKS_CACHE_KEY = 'myFitBeats_training_blocks_cache';

export function useTrainingBlocks() {
  return useQuery<TrainingBlock[]>({
    queryKey: ['/api/training-blocks'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/training-blocks');
      const data = await response.json();
      try {
        localStorage.setItem(TRAINING_BLOCKS_CACHE_KEY, JSON.stringify(data));
      } catch (e) {
        // localStorage might be full or unavailable
      }
      return data;
    },
    staleTime: 30000,
    // Keep previous data or fall back to localStorage cache to prevent flash
    placeholderData: (previousData) => {
      if (previousData) return previousData;
      try {
        const cached = localStorage.getItem(TRAINING_BLOCKS_CACHE_KEY);
        return cached ? JSON.parse(cached) : [];
      } catch {
        return [];
      }
    },
  });
}

export function TrainingBlocksDialog({
  open,
  onOpenChange,
  initialBlockId,
  isFullPage = false,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  initialBlockId?: string | null;
  isFullPage?: boolean;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const userHasAccess = user && hasActiveAccess(user as any);
  const { data: blocks = [], isLoading } = useTrainingBlocks();
  
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingBlock, setEditingBlock] = useState<TrainingBlock | null>(null);
  const [blockName, setBlockName] = useState("");
  const [category, setCategory] = useState<TrainingBlockCategory>("general");
  const [startDate, setStartDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [durationWeeks, setDurationWeeks] = useState("12");
  const [durationMode, setDurationMode] = useState<"weeks" | "dates">("weeks");
  const [endDate, setEndDate] = useState<string>(format(addWeeks(new Date(), 12), 'yyyy-MM-dd'));
  const [selectedPrograms, setSelectedPrograms] = useState<string[]>([]);
  const [notes, setNotes] = useState("");
  const [deloadEnabled, setDeloadEnabled] = useState(false);
  const [deloadWeeks, setDeloadWeeks] = useState<number[]>([]);
  const [deloadPreset, setDeloadPreset] = useState<DeloadPresetId>('moderate');
  const [weightReduction, setWeightReduction] = useState(40);
  const [repsReduction, setRepsReduction] = useState(0);
  const [setsReduction, setSetsReduction] = useState(50);
  
  const applyDeloadPreset = (presetId: DeloadPresetId) => {
    const preset = DELOAD_PRESETS.find(p => p.id === presetId);
    if (preset) {
      setDeloadPreset(presetId);
      if (presetId !== 'custom') {
        setWeightReduction(preset.weight);
        setRepsReduction(preset.reps);
        setSetsReduction(preset.sets);
      }
    }
  };

  const customPrograms: CustomProgram[] = (() => {
    try {
      const stored = localStorage.getItem("customPrograms");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  })();
  
  const { apiPrograms } = useApiPrograms();
  const allPrograms = useMemo(() => [...apiPrograms, ...customPrograms], [apiPrograms, customPrograms]);
  
  // Get programs from the new membership system
  const myProgramIds: string[] = (() => {
    try {
      const memberships = localStorage.getItem("programMemberships");
      if (memberships) {
        const parsed = JSON.parse(memberships);
        // Include current and favorite programs (not archived)
        return parsed
          .filter((m: any) => m.status === 'current' || m.status === 'favorite')
          .map((m: any) => m.programId);
      }
      // Fallback to legacy format
      const stored = localStorage.getItem("myPrograms");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  })();
  const myPrograms = allPrograms.filter((p: Program) => myProgramIds.includes(p.id));

  const createBlockMutation = useMutation({
    mutationFn: async (data: any) => {
      if (!isOnline()) {
        const tempId = `temp_block_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        queueTrainingBlock(data, tempId);
        return { ...data, id: tempId, queued: true };
      }
      const response = await apiRequest('POST', '/api/training-blocks', data);
      return response.json();
    },
    onSuccess: (result) => {
      if (!result?.queued) {
        queryClient.invalidateQueries({ queryKey: ['/api/training-blocks'] });
      }
      toast({ title: result?.queued ? "Training block saved (will sync when online)" : "Training block created!" });
      resetForm();
    },
    onError: () => {
      toast({ title: "Failed to create training block", variant: "destructive" });
    }
  });

  const updateBlockMutation = useMutation({
    mutationFn: async ({ id, ...data }: any) => {
      if (!isOnline()) {
        queueTrainingBlockUpdate(id, data);
        return { id, ...data, queued: true };
      }
      const response = await apiRequest('PATCH', `/api/training-blocks/${id}`, data);
      return response.json();
    },
    onSuccess: (result) => {
      if (!result?.queued) {
        queryClient.invalidateQueries({ queryKey: ['/api/training-blocks'] });
      }
      toast({ title: result?.queued ? "Changes saved (will sync when online)" : "Training block updated!" });
      resetForm();
    },
    onError: () => {
      toast({ title: "Failed to update training block", variant: "destructive" });
    }
  });

  const deleteBlockMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!isOnline()) {
        queueTrainingBlockDelete(id);
        return { queued: true };
      }
      await apiRequest('DELETE', `/api/training-blocks/${id}`);
      return { queued: false };
    },
    onSuccess: (result) => {
      if (!result?.queued) {
        queryClient.invalidateQueries({ queryKey: ['/api/training-blocks'] });
      }
      toast({ title: result?.queued ? "Deletion saved (will sync when online)" : "Training block deleted" });
    },
    onError: () => {
      toast({ title: "Failed to delete training block", variant: "destructive" });
    }
  });

  const resetForm = () => {
    setShowCreateForm(false);
    setEditingBlock(null);
    setBlockName("");
    setCategory("general");
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    setStartDate(todayStr);
    setDurationWeeks("12");
    setDurationMode("weeks");
    const futureDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 84); // 12 weeks
    const futureStr = `${futureDate.getFullYear()}-${String(futureDate.getMonth() + 1).padStart(2, '0')}-${String(futureDate.getDate()).padStart(2, '0')}`;
    setEndDate(futureStr);
    setSelectedPrograms([]);
    setNotes("");
    setDeloadEnabled(false);
    setDeloadWeeks([]);
    setDeloadPreset('moderate');
    setWeightReduction(40);
    setRepsReduction(0);
    setSetsReduction(50);
  };

  const checkForOverlap = (newStart: Date, newEnd: Date, excludeBlockId?: string): TrainingBlock | null => {
    for (const block of blocks) {
      if (excludeBlockId && block.id === excludeBlockId) continue;
      if (block.status === "completed" || block.status === "archived") continue;
      
      // Use UTC comparison to avoid timezone issues
      const blockStartUTC = new Date(block.startDate).getTime();
      const blockEndUTC = new Date(block.endDate).getTime();
      const newStartUTC = newStart.getTime();
      const newEndUTC = newEnd.getTime();
      
      const overlaps = (newStartUTC <= blockEndUTC && newEndUTC >= blockStartUTC);
      if (overlaps) {
        return block;
      }
    }
    return null;
  };

  const handleSubmit = () => {
    if (!blockName.trim()) {
      toast({ title: "Please enter a block name", variant: "destructive" });
      return;
    }

    if (deloadEnabled && deloadWeeks.length === 0) {
      toast({ title: "Please select at least one deload week", variant: "destructive" });
      return;
    }

    // Use UTC date utilities to avoid timezone edge cases
    // Dates are stored at midnight UTC and formatted using UTC-aware functions
    const startDateObj = dateStringToUTC(startDate);
    
    let endDateObj: Date;
    if (durationMode === "dates") {
      endDateObj = dateStringToUTC(endDate);
      if (compareDatesUTC(endDateObj, startDateObj) <= 0) {
        toast({ title: "End date must be after start date", variant: "destructive" });
        return;
      }
    } else {
      endDateObj = addWeeksUTC(startDateObj, parseInt(durationWeeks));
    }

    const overlappingBlock = checkForOverlap(startDateObj, endDateObj, editingBlock?.id);
    if (overlappingBlock) {
      const overlapStart = formatDateDisplay(overlappingBlock.startDate, 'short');
      const overlapEnd = formatDateDisplay(overlappingBlock.endDate, 'medium');
      toast({ 
        title: "Dates overlap with existing block", 
        description: `"${overlappingBlock.name}" runs from ${overlapStart} to ${overlapEnd}. Please choose different dates.`,
        variant: "destructive" 
      });
      return;
    }
    
    const data = {
      name: blockName,
      category,
      startDate: startDateObj.toISOString(),
      endDate: endDateObj.toISOString(),
      status: "active",
      programIds: selectedPrograms.length > 0 ? selectedPrograms : null,
      notes: notes || null,
      deloadSettings: deloadEnabled ? {
        enabled: true,
        deloadWeeks,
        weightReduction,
        repsReduction,
        setsReduction,
      } : null,
    };

    if (editingBlock) {
      updateBlockMutation.mutate({ id: editingBlock.id, ...data });
    } else {
      createBlockMutation.mutate(data);
    }
  };

  const handleEdit = (block: TrainingBlock) => {
    setEditingBlock(block);
    setBlockName(block.name);
    setCategory(block.category || "general");
    setStartDate(formatDateForInput(block.startDate));
    setEndDate(formatDateForInput(block.endDate));
    const weeks = differenceInWeeksUTC(block.endDate, block.startDate);
    setDurationWeeks(String(Math.max(1, weeks)));
    setDurationMode("dates");
    setSelectedPrograms(block.programIds || []);
    setNotes(block.notes || "");
    const deload = block.deloadSettings || DEFAULT_DELOAD_SETTINGS;
    setDeloadEnabled(deload.enabled);
    setDeloadWeeks(deload.deloadWeeks || []);
    setWeightReduction(deload.weightReduction);
    setRepsReduction(deload.repsReduction);
    setSetsReduction(deload.setsReduction);
    // Detect matching preset or set to custom
    const matchingPreset = DELOAD_PRESETS.find(
      p => p.id !== 'custom' && p.weight === deload.weightReduction && p.reps === deload.repsReduction && p.sets === deload.setsReduction
    );
    setDeloadPreset(matchingPreset?.id || 'custom');
    setShowCreateForm(true);
  };

  // Auto-open specific block for editing when initialBlockId is provided
  useEffect(() => {
    if (open && initialBlockId && blocks.length > 0) {
      const blockToEdit = blocks.find(b => b.id === initialBlockId);
      if (blockToEdit && !showCreateForm) {
        handleEdit(blockToEdit);
      }
    }
  }, [open, initialBlockId, blocks]);

  const handleExtend = (block: TrainingBlock) => {
    // Use UTC-aware date extension to maintain consistency
    const newEndDate = addWeeksUTC(block.endDate, 2);
    updateBlockMutation.mutate({
      id: block.id,
      endDate: newEndDate.toISOString(),
      status: "extended"
    });
  };

  // Confirmation dialog state
  const [confirmAction, setConfirmAction] = useState<{ type: 'complete' | 'archive'; block: TrainingBlock; isEarly: boolean } | null>(null);
  const [showCelebration, setShowCelebration] = useState<TrainingBlock | null>(null);
  const [completedOpen, setCompletedOpen] = useState(false);
  const [archivedOpen, setArchivedOpen] = useState(false);

  const isBlockEndedNaturally = (block: TrainingBlock) => {
    // Compare using UTC dates to avoid timezone issues
    const endDate = new Date(block.endDate);
    const now = new Date();
    const endUTC = Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), endDate.getUTCDate());
    const todayUTC = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
    return todayUTC > endUTC;
  };

  const handleCompleteClick = (block: TrainingBlock) => {
    const isEarly = !isBlockEndedNaturally(block);
    if (isEarly) {
      setConfirmAction({ type: 'complete', block, isEarly: true });
    } else {
      doComplete(block, true);
    }
  };

  const handleArchiveClick = (block: TrainingBlock) => {
    const isEarly = !isBlockEndedNaturally(block);
    if (isEarly) {
      setConfirmAction({ type: 'archive', block, isEarly: true });
    } else {
      doArchive(block);
    }
  };

  const doComplete = (block: TrainingBlock, showCelebrate: boolean) => {
    updateBlockMutation.mutate({
      id: block.id,
      status: "completed"
    }, {
      onSuccess: () => {
        if (showCelebrate && isBlockEndedNaturally(block)) {
          setShowCelebration(block);
        }
      }
    });
  };

  const doArchive = (block: TrainingBlock) => {
    updateBlockMutation.mutate({
      id: block.id,
      status: "archived"
    });
  };

  const handleContinue = (block: TrainingBlock) => {
    updateBlockMutation.mutate({
      id: block.id,
      status: "active"
    });
    toast({ title: "Block Resumed", description: `"${block.name}" is now active again!` });
  };

  const toggleProgram = (programId: string) => {
    setSelectedPrograms(prev =>
      prev.includes(programId)
        ? prev.filter(id => id !== programId)
        : [...prev, programId]
    );
  };

  const activeBlocks = blocks.filter(b => b.status === "active" || b.status === "extended");
  const completedBlocks = blocks.filter(b => b.status === "completed");
  const archivedBlocks = blocks.filter(b => b.status === "archived");

  const contentElement = (
    <div className={isFullPage ? "" : "flex flex-col min-w-0 w-full max-w-full overflow-x-hidden"}>
      {!isFullPage && (
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" />
            Training Blocks
          </DialogTitle>
          <DialogDescription>
            Organize your training into focused blocks with specific programs and timeframes.
          </DialogDescription>
        </DialogHeader>
      )}
      {isFullPage && (
        <div className="mb-4">
          <p className="text-sm text-muted-foreground">
            Organize your training into focused blocks with specific programs and timeframes.
          </p>
        </div>
      )}

        {showCreateForm || editingBlock ? (
          <div className="space-y-4 overflow-y-auto overflow-x-hidden flex-1 min-w-0 w-full max-w-full px-0.5">
            <div>
              <Label htmlFor="block-name">Block Name</Label>
              <Input
                id="block-name"
                placeholder="e.g., Strength Phase, Competition Prep"
                value={blockName}
                onChange={(e) => setBlockName(e.target.value)}
                className="mt-1"
                data-testid="input-block-name"
              />
            </div>

            <div>
              <Label>Training Focus</Label>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {TRAINING_BLOCK_CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setCategory(cat.id)}
                    className={cn(
                      "p-2.5 rounded-lg border text-left transition-all",
                      category === cat.id
                        ? `${CATEGORY_COLORS[cat.id]} border-2`
                        : "border-border bg-card/50 text-muted-foreground hover:border-primary/50"
                    )}
                    data-testid={`category-${cat.id}`}
                  >
                    <div className="flex items-center gap-2">
                      {CATEGORY_ICONS[cat.id]}
                      <span className="text-sm font-medium">{cat.label}</span>
                    </div>
                    <p className="text-xs opacity-70 mt-0.5 line-clamp-1">{cat.description}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="start-date">Start Date</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="mt-1 w-full [&::-webkit-calendar-picker-indicator]:filter [&::-webkit-calendar-picker-indicator]:invert"
                  data-testid="input-start-date"
                />
              </div>
              
              {/* Duration Mode Toggle */}
              <div>
                <Label className="mb-2 block">Duration Type</Label>
                <div className="flex rounded-lg border border-border overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setDurationMode("weeks")}
                    className={cn(
                      "flex-1 py-2 px-3 text-sm font-medium transition-colors",
                      durationMode === "weeks"
                        ? "bg-primary text-primary-foreground"
                        : "bg-card text-muted-foreground hover:bg-card/80"
                    )}
                    data-testid="button-duration-weeks"
                  >
                    <Clock className="w-3.5 h-3.5 inline mr-1.5" />
                    Weeks
                  </button>
                  <button
                    type="button"
                    onClick={() => setDurationMode("dates")}
                    className={cn(
                      "flex-1 py-2 px-3 text-sm font-medium transition-colors",
                      durationMode === "dates"
                        ? "bg-primary text-primary-foreground"
                        : "bg-card text-muted-foreground hover:bg-card/80"
                    )}
                    data-testid="button-duration-dates"
                  >
                    <Calendar className="w-3.5 h-3.5 inline mr-1.5" />
                    End Date
                  </button>
                </div>
              </div>

              {durationMode === "weeks" ? (
                <div>
                  <Label htmlFor="duration">Duration</Label>
                  <Select value={durationWeeks} onValueChange={(v) => {
                    setDurationWeeks(v);
                    setDeloadWeeks(prev => prev.filter(w => w <= parseInt(v)));
                  }}>
                    <SelectTrigger className="mt-1 w-full" data-testid="select-duration">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {Array.from({ length: 52 }, (_, i) => i + 1).map(w => (
                        <SelectItem key={w} value={String(w)}>{w} week{w > 1 ? 's' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div>
                  <Label htmlFor="end-date">End Date</Label>
                  <Input
                    id="end-date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate}
                    className="mt-1 w-full [&::-webkit-calendar-picker-indicator]:filter [&::-webkit-calendar-picker-indicator]:invert"
                    data-testid="input-end-date"
                  />
                  {startDate && endDate && new Date(endDate) > new Date(startDate) && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {Math.round(differenceInWeeks(new Date(endDate), new Date(startDate)))} weeks
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-3 p-3 rounded-lg border border-border/50 bg-card/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-amber-400" />
                  <Label htmlFor="deload-enabled" className="text-sm font-medium">Schedule Deload Weeks</Label>
                </div>
                <Switch
                  id="deload-enabled"
                  checked={deloadEnabled}
                  onCheckedChange={setDeloadEnabled}
                  data-testid="switch-deload-enabled"
                />
              </div>
              
              {deloadEnabled && (() => {
                const effectiveWeeks = durationMode === "dates" 
                  ? Math.max(1, Math.round(differenceInWeeks(new Date(endDate), new Date(startDate))))
                  : parseInt(durationWeeks);
                return (
                <div className="space-y-4 pt-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">Select Deload Weeks</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {Array.from({ length: effectiveWeeks }, (_, i) => i + 1).map(week => (
                        <button
                          key={week}
                          type="button"
                          onClick={() => {
                            setDeloadWeeks(prev =>
                              prev.includes(week)
                                ? prev.filter(w => w !== week)
                                : [...prev, week].sort((a, b) => a - b)
                            );
                          }}
                          className={cn(
                            "w-8 h-8 rounded-full text-xs font-medium transition-all",
                            deloadWeeks.includes(week)
                              ? "bg-amber-500/30 text-amber-700 dark:text-amber-300 border-2 border-amber-500"
                              : "bg-card border border-border text-muted-foreground hover:border-amber-500/50"
                          )}
                          data-testid={`deload-week-${week}`}
                        >
                          {week}
                        </button>
                      ))}
                    </div>
                    {deloadWeeks.length > 0 && (
                      <p className="text-xs text-amber-400 mt-2">
                        Deload on week{deloadWeeks.length > 1 ? 's' : ''}: {deloadWeeks.join(', ')}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Deload Intensity</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {DELOAD_PRESETS.map(preset => (
                        <button
                          key={preset.id}
                          type="button"
                          onClick={() => applyDeloadPreset(preset.id)}
                          className={cn(
                            "p-2 rounded-lg border text-center transition-all",
                            deloadPreset === preset.id
                              ? "bg-amber-500/20 border-amber-500 text-amber-700 dark:text-amber-300"
                              : "bg-card border-border text-muted-foreground hover:border-amber-500/50"
                          )}
                          data-testid={`deload-preset-${preset.id}`}
                        >
                          <span className="text-sm font-medium block">{preset.label}</span>
                          <span className="text-xs opacity-70">{preset.description}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {deloadPreset === 'custom' && (
                    <div className="space-y-3 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
                      <Label className="text-xs text-amber-600 dark:text-amber-400">Custom Reductions</Label>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground">Weight %</label>
                          <Input
                            type="number"
                            min={0}
                            max={70}
                            value={weightReduction}
                            onChange={(e) => setWeightReduction(Math.min(70, Math.max(0, parseInt(e.target.value) || 0)))}
                            className="h-9 text-center"
                            data-testid="input-custom-weight"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground">Reps %</label>
                          <Input
                            type="number"
                            min={0}
                            max={50}
                            value={repsReduction}
                            onChange={(e) => setRepsReduction(Math.min(50, Math.max(0, parseInt(e.target.value) || 0)))}
                            className="h-9 text-center"
                            data-testid="input-custom-reps"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground">Sets %</label>
                          <Input
                            type="number"
                            min={0}
                            max={50}
                            value={setsReduction}
                            onChange={(e) => setSetsReduction(Math.min(50, Math.max(0, parseInt(e.target.value) || 0)))}
                            className="h-9 text-center"
                            data-testid="input-custom-sets"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Reduce weight by {weightReduction}%, reps by {repsReduction}%, sets by {setsReduction}%
                      </p>
                    </div>
                  )}
                </div>
                );
              })()}
            </div>

            {myPrograms.length > 0 && (
              <div>
                <Label>Include Programs (Optional)</Label>
                <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                  {myPrograms.map((program: Program) => (
                    <button
                      key={program.id}
                      onClick={() => toggleProgram(program.id)}
                      className={cn(
                        "w-full p-2 rounded-lg border text-left flex items-center gap-2 transition-colors",
                        selectedPrograms.includes(program.id)
                          ? "border-primary bg-primary/10 text-foreground"
                          : "border-border bg-card/50 text-muted-foreground hover:border-primary/50"
                      )}
                      data-testid={`toggle-program-${program.id}`}
                    >
                      <Dumbbell className="w-4 h-4" />
                      <span className="flex-1 truncate">{program.title}</span>
                      {selectedPrograms.includes(program.id) && (
                        <Check className="w-4 h-4 text-primary" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Input
                id="notes"
                placeholder="Goals, focus areas, etc."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-1"
                data-testid="input-notes"
              />
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={resetForm}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={createBlockMutation.isPending || updateBlockMutation.isPending}
                data-testid="button-save-block"
              >
                {editingBlock ? "Update Block" : "Create Block"}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="flex flex-col min-h-0 flex-1 px-0.5">
            {userHasAccess ? (
              <Button
                onClick={() => setShowCreateForm(true)}
                className="w-full shrink-0 focus-visible:ring-0"
                data-testid="button-new-block"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Training Block
              </Button>
            ) : (
              <div className="text-center p-4 rounded-lg border border-border bg-muted/50">
                <p className="font-medium mb-2">Subscription Required</p>
                <p className="text-sm text-muted-foreground mb-3">Start your 14-day free trial to create training blocks.</p>
                <Button onClick={() => setLocation('/preferences')} size="sm">
                  Start Free Trial
                </Button>
              </div>
            )}

            <div className="overflow-y-auto overflow-x-hidden mt-4 space-y-4 flex-1 min-w-0">
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : blocks.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No training blocks yet</p>
                <p className="text-sm">{userHasAccess ? "Create a block to organize your training phases" : "Start your free trial to create training blocks"}</p>
              </div>
            ) : (
              <>
                {activeBlocks.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-2">Active Blocks</h4>
                    <div className="space-y-2">
                      {activeBlocks.map(block => (
                        <TrainingBlockCard
                          key={block.id}
                          block={block}
                          programs={allPrograms}
                          onEdit={() => handleEdit(block)}
                          onExtend={() => handleExtend(block)}
                          onComplete={() => handleCompleteClick(block)}
                          onArchive={() => handleArchiveClick(block)}
                          onDelete={() => deleteBlockMutation.mutate(block.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {completedBlocks.length > 0 && (
                  <Collapsible open={completedOpen} onOpenChange={setCompletedOpen}>
                    <CollapsibleTrigger className="flex items-center justify-between w-full py-2 px-3 rounded-lg bg-green-500/5 border border-green-500/20 hover:bg-green-500/10 transition-colors">
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-500" />
                        <span className="text-sm font-medium text-muted-foreground">Completed Blocks</span>
                        <span className="text-xs bg-green-500/10 text-green-500 px-1.5 py-0.5 rounded">{completedBlocks.length}</span>
                      </div>
                      {completedOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pt-2">
                      <div className="space-y-2">
                        {completedBlocks.slice(0, 3).map(block => (
                          <TrainingBlockCard
                            key={block.id}
                            block={block}
                            programs={allPrograms}
                            onEdit={() => handleEdit(block)}
                            onContinue={() => handleContinue(block)}
                            onDelete={() => deleteBlockMutation.mutate(block.id)}
                            isCompleted
                          />
                        ))}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}

                {archivedBlocks.length > 0 && (
                  <Collapsible open={archivedOpen} onOpenChange={setArchivedOpen}>
                    <CollapsibleTrigger className="flex items-center justify-between w-full py-2 px-3 rounded-lg bg-muted/30 border border-border/50 hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-2">
                        <Archive className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium text-muted-foreground">Archived Blocks</span>
                        <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded">{archivedBlocks.length}</span>
                      </div>
                      {archivedOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pt-2">
                      <div className="space-y-2">
                        {archivedBlocks.slice(0, 3).map(block => (
                          <TrainingBlockCard
                            key={block.id}
                            block={block}
                            programs={allPrograms}
                            onEdit={() => handleEdit(block)}
                            onDelete={() => deleteBlockMutation.mutate(block.id)}
                            isArchived
                          />
                        ))}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </>
            )}
            </div>
          </div>
        )}
    </div>
  );

  return (
    <>
    {isFullPage ? (
      contentElement
    ) : (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-card border-border w-full max-w-[min(420px,calc(100vw-2rem))] sm:max-w-lg max-h-[80vh] sm:max-h-[85vh] flex flex-col p-4 sm:p-6 overflow-x-hidden overflow-y-auto [&>*]:max-w-full">
          {contentElement}
        </DialogContent>
      </Dialog>
    )}
    
    {/* Confirmation Dialog for Early Completion/Archive - rendered outside parent Dialog */}
    <AlertDialog open={!!confirmAction} onOpenChange={(open) => !open && setConfirmAction(null)}>
      <AlertDialogContent className="bg-card border-border">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-foreground">
            {confirmAction?.type === 'complete' ? 'Complete Training Block Early?' : 'Archive Training Block Early?'}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {confirmAction?.block && (
              <>
                <span className="font-medium text-foreground">"{confirmAction.block.name}"</span> hasn't reached its scheduled end date yet.
                {confirmAction.type === 'complete' 
                  ? " Are you sure you want to mark it as completed?"
                  : " Are you sure you want to archive it?"}
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              if (confirmAction) {
                if (confirmAction.type === 'complete') {
                  doComplete(confirmAction.block, false);
                } else {
                  doArchive(confirmAction.block);
                }
                setConfirmAction(null);
              }
            }}
          >
            {confirmAction?.type === 'complete' ? 'Complete Anyway' : 'Archive Anyway'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Celebration Dialog for Natural Completion - rendered outside parent Dialog */}
    <Dialog open={!!showCelebration} onOpenChange={(open) => !open && setShowCelebration(null)}>
      <DialogContent className="bg-card border-border max-w-sm text-center">
        <DialogHeader className="sr-only">
          <DialogTitle>Training Block Complete</DialogTitle>
        </DialogHeader>
        <AnimatePresence>
          {showCelebration && (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="py-4"
            >
              <motion.div
                animate={{ rotate: [0, -10, 10, -10, 10, 0], scale: [1, 1.1, 1] }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-400 via-yellow-500 to-orange-500 flex items-center justify-center"
              >
                <Trophy className="w-10 h-10 text-white" />
              </motion.div>
              
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <h2 className="text-2xl font-bold text-foreground mb-2">
                  Training Block Complete!
                </h2>
                <p className="text-lg font-medium text-primary mb-1">
                  {showCelebration.name}
                </p>
                <p className="text-muted-foreground text-sm mb-4">
                  You crushed {Math.round(differenceInWeeks(new Date(showCelebration.endDate), new Date(showCelebration.startDate)))} weeks of training!
                </p>
              </motion.div>

              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="flex justify-center gap-2 mb-4"
              >
                {[...Array(5)].map((_, i) => (
                  <motion.div
                    key={i}
                    animate={{ 
                      y: [0, -10, 0],
                      rotate: [0, 15, -15, 0]
                    }}
                    transition={{ 
                      duration: 0.5, 
                      delay: 0.6 + i * 0.1,
                      repeat: 1
                    }}
                  >
                    <Star className="w-6 h-6 text-yellow-500 fill-yellow-500" />
                  </motion.div>
                ))}
              </motion.div>

              <Button 
                onClick={() => setShowCelebration(null)}
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
              >
                Awesome!
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
    </>
  );
}

function TrainingBlockCard({
  block,
  programs,
  onEdit,
  onExtend,
  onComplete,
  onArchive,
  onContinue,
  onDelete,
  isCompleted,
  isArchived,
}: {
  block: TrainingBlock;
  programs: Program[];
  onEdit: () => void;
  onExtend?: () => void;
  onComplete?: () => void;
  onArchive?: () => void;
  onContinue?: () => void;
  onDelete: () => void;
  isCompleted?: boolean;
  isArchived?: boolean;
}) {
  // Use UTC dates to avoid timezone issues - training block dates are stored at midnight UTC
  // Note: endDate is stored as exclusive boundary (first day after block ends)
  const blockStart = new Date(block.startDate);
  const blockEnd = new Date(block.endDate);
  const now = new Date();
  
  // Extract UTC date components for comparison
  const startUTC = Date.UTC(blockStart.getUTCFullYear(), blockStart.getUTCMonth(), blockStart.getUTCDate());
  const endUTC = Date.UTC(blockEnd.getUTCFullYear(), blockEnd.getUTCMonth(), blockEnd.getUTCDate());
  const todayUTC = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  
  const isActive = todayUTC >= startUTC && todayUTC < endUTC;
  
  // Calculate duration using UTC dates (ms per day)
  const msPerDay = 24 * 60 * 60 * 1000;
  const totalDays = Math.max(1, Math.round((endUTC - startUTC) / msPerDay));
  const daysPassed = Math.max(1, Math.min(totalDays, Math.round((todayUTC - startUTC) / msPerDay) + 1));
  const isShortBlock = totalDays < 7;
  
  // Calculate weeks from totalDays (should be exact multiple of 7 for week-based blocks)
  const weeksTotal = isShortBlock ? totalDays : Math.max(1, Math.round(totalDays / 7));
  const weeksPassed = isShortBlock ? daysPassed : Math.max(1, Math.min(weeksTotal, Math.ceil(daysPassed / 7)));
  const progress = Math.min(100, Math.max(0, isShortBlock ? (daysPassed / totalDays) * 100 : (weeksPassed / weeksTotal) * 100));

  const blockPrograms = block.programIds
    ? programs.filter(p => block.programIds?.includes(p.id))
    : [];

  const categoryInfo = TRAINING_BLOCK_CATEGORIES.find(c => c.id === block.category) || TRAINING_BLOCK_CATEGORIES[0];
  const categoryColor = CATEGORY_COLORS[block.category] || CATEGORY_COLORS.general;
  const calendarColor = CATEGORY_CALENDAR_COLORS[block.category] || CATEGORY_CALENDAR_COLORS.general;
  const [, navigate] = useLocation();

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button')) return;
    navigate(`/training-block/${block.id}`);
  };

  const durationLabel = isShortBlock 
    ? `${totalDays} day${totalDays !== 1 ? 's' : ''}`
    : `${weeksTotal} week${weeksTotal !== 1 ? 's' : ''}`;
  
  const progressLabel = isShortBlock
    ? `Day ${daysPassed} of ${totalDays}`
    : `Week ${weeksPassed} of ${weeksTotal}`;

  return (
    <Card 
      className={cn(
        "p-4 cursor-pointer transition-all hover:shadow-lg hover:scale-[1.01] bg-card",
        "border border-border/50",
        isActive && !isCompleted && "border-l-4 border-l-primary rounded-l-sm shadow-sm",
        isCompleted && "opacity-75",
        isArchived && "opacity-60"
      )}
      onClick={handleCardClick}
      data-testid={`card-training-block-${block.id}`}
    >
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h5 className="font-semibold text-foreground text-base leading-tight">{block.name}</h5>
          </div>
          <div className="flex items-center gap-0.5 shrink-0">
            {!isCompleted && !isArchived && (
              <>
                {onExtend && (
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onExtend} title="Extend 2 weeks">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                  </Button>
                )}
                {onComplete && (
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onComplete} title="Mark complete">
                    <Check className="w-4 h-4 text-muted-foreground" />
                  </Button>
                )}
                {onArchive && (
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onArchive} title="Archive">
                    <Archive className="w-4 h-4 text-muted-foreground" />
                  </Button>
                )}
              </>
            )}
            {isCompleted && onContinue && (
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onContinue} title="Continue this block">
                <RotateCcw className="w-4 h-4 text-primary" />
              </Button>
            )}
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onEdit}>
              <Edit className="w-4 h-4 text-muted-foreground" />
            </Button>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onDelete}>
              <Trash2 className="w-4 h-4 text-destructive" />
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className={cn("text-xs px-2.5 py-1 rounded-full flex items-center gap-1.5 border font-medium", categoryColor)}>
            {CATEGORY_ICONS[block.category]}
            {categoryInfo.label}
          </span>
          {block.status === "extended" && (
            <span className="text-xs px-2 py-1 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 font-medium">Extended</span>
          )}
          {isCompleted && (
            <span className="text-xs px-2 py-1 rounded-full bg-green-500/15 text-green-600 dark:text-green-400 font-medium">Completed</span>
          )}
          {isArchived && (
            <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground font-medium">Archived</span>
          )}
          {block.deloadSettings?.enabled && block.deloadSettings.deloadWeeks.length > 0 && (
            isDeloadWeek(block) ? (
              <span className="text-xs px-2.5 py-1.5 rounded-full bg-amber-500/30 text-amber-600 dark:text-amber-300 flex items-center gap-1.5 font-semibold border border-amber-500/50 animate-pulse">
                <Shield className="w-3.5 h-3.5" />
                Deload Week
              </span>
            ) : (
              <span className="text-xs px-2 py-1 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-300 flex items-center gap-1 font-medium">
                <Shield className="w-3 h-3" />
                Deload W{block.deloadSettings.deloadWeeks.join(', ')}
              </span>
            )
          )}
        </div>

        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="w-4 h-4" />
            <span>{formatDateDisplay(block.startDate, 'short')} - {formatDateDisplay(addDaysUTC(block.endDate, -1), 'medium')}</span>
          </div>
          <span className="text-primary font-medium">{durationLabel}</span>
        </div>

        {blockPrograms.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {blockPrograms.map(p => (
              <span key={p.id} className="text-xs px-2.5 py-1 rounded-full bg-secondary text-secondary-foreground">
                {p.title}
              </span>
            ))}
          </div>
        )}

        {!isCompleted && isActive && (
          <div className="pt-1">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
              <span>{progressLabel}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-2 bg-secondary rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary transition-all rounded-full"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

export function getBlockForDate(blocks: TrainingBlock[], date: Date): TrainingBlock | undefined {
  // Compare using UTC dates to avoid timezone issues
  // Note: endDate is stored as exclusive boundary, so use strictly less than
  const dateUTC = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  
  return blocks.find(block => {
    const blockStart = new Date(block.startDate);
    const blockEnd = new Date(block.endDate);
    const startUTC = Date.UTC(blockStart.getUTCFullYear(), blockStart.getUTCMonth(), blockStart.getUTCDate());
    const endUTC = Date.UTC(blockEnd.getUTCFullYear(), blockEnd.getUTCMonth(), blockEnd.getUTCDate());
    
    return dateUTC >= startUTC && dateUTC < endUTC && block.status !== "completed";
  });
}

export const CATEGORY_CALENDAR_COLORS: Record<TrainingBlockCategory, { bg: string; border: string; text: string; accent: string; dot: string }> = {
  general: { bg: "bg-sky-500/10 dark:bg-sky-500/20", border: "border-sky-400/40", text: "text-sky-700 dark:text-sky-300", accent: "sky", dot: "bg-sky-500" },
  hypertrophy: { bg: "bg-orange-500/10 dark:bg-orange-500/20", border: "border-orange-400/40", text: "text-orange-700 dark:text-orange-300", accent: "orange", dot: "bg-orange-500" },
  strength: { bg: "bg-violet-500/10 dark:bg-violet-500/20", border: "border-violet-400/40", text: "text-violet-700 dark:text-violet-300", accent: "violet", dot: "bg-violet-500" },
  conditioning: { bg: "bg-emerald-500/10 dark:bg-emerald-500/20", border: "border-emerald-400/40", text: "text-emerald-700 dark:text-emerald-300", accent: "emerald", dot: "bg-emerald-500" },
  athletic: { bg: "bg-cyan-500/10 dark:bg-cyan-500/20", border: "border-cyan-400/40", text: "text-cyan-700 dark:text-cyan-300", accent: "cyan", dot: "bg-cyan-500" },
  longevity: { bg: "bg-rose-500/10 dark:bg-rose-500/20", border: "border-rose-400/40", text: "text-rose-700 dark:text-rose-300", accent: "rose", dot: "bg-rose-500" },
  rest_recovery: { bg: "bg-teal-500/10 dark:bg-teal-500/20", border: "border-teal-400/40", text: "text-teal-700 dark:text-teal-300", accent: "teal", dot: "bg-teal-500" },
  weight_loss: { bg: "bg-amber-500/10 dark:bg-amber-500/20", border: "border-amber-400/40", text: "text-amber-700 dark:text-amber-300", accent: "amber", dot: "bg-amber-500" },
  maintenance: { bg: "bg-slate-500/10 dark:bg-slate-500/20", border: "border-slate-400/40", text: "text-slate-700 dark:text-slate-300", accent: "slate", dot: "bg-slate-500" },
  technique: { bg: "bg-fuchsia-500/10 dark:bg-fuchsia-500/20", border: "border-fuchsia-400/40", text: "text-fuchsia-700 dark:text-fuchsia-300", accent: "fuchsia", dot: "bg-fuchsia-500" },
  other: { bg: "bg-indigo-500/10 dark:bg-indigo-500/20", border: "border-indigo-400/40", text: "text-indigo-700 dark:text-indigo-300", accent: "indigo", dot: "bg-indigo-500" },
};

export function getBlockColorIndex(block: TrainingBlock): number {
  const categories: TrainingBlockCategory[] = ['general', 'hypertrophy', 'strength', 'conditioning', 'athletic', 'longevity', 'rest_recovery', 'weight_loss', 'maintenance', 'technique'];
  const idx = categories.indexOf(block.category);
  return idx >= 0 ? idx : 0;
}

export function getBlockColor(block: TrainingBlock): string {
  const color = CATEGORY_CALENDAR_COLORS[block.category] || CATEGORY_CALENDAR_COLORS.general;
  return `${color.bg} ${color.border}`;
}

export function getBlockBorderOnly(block: TrainingBlock): string {
  const color = CATEGORY_CALENDAR_COLORS[block.category] || CATEGORY_CALENDAR_COLORS.general;
  return color.border;
}

export function getBlockBgOnly(block: TrainingBlock): string {
  const color = CATEGORY_CALENDAR_COLORS[block.category] || CATEGORY_CALENDAR_COLORS.general;
  return color.bg;
}


export function getBlockPositionStyle(
  blocks: TrainingBlock[],
  date: Date,
  block: TrainingBlock
): { isStart: boolean; isEnd: boolean; colorIndex: number } {
  // Compare using UTC dates to avoid timezone issues
  // Note: endDate is stored as exclusive boundary, so the last day is endDate - 1 day
  const blockStart = new Date(block.startDate);
  const blockEnd = new Date(block.endDate);
  
  const startUTC = Date.UTC(blockStart.getUTCFullYear(), blockStart.getUTCMonth(), blockStart.getUTCDate());
  const endUTC = Date.UTC(blockEnd.getUTCFullYear(), blockEnd.getUTCMonth(), blockEnd.getUTCDate());
  const msPerDay = 24 * 60 * 60 * 1000;
  const lastDayUTC = endUTC - msPerDay; // Inclusive last day
  const currentUTC = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  
  const isStart = currentUTC === startUTC;
  const isEnd = currentUTC === lastDayUTC;
  
  return {
    isStart,
    isEnd,
    colorIndex: getBlockColorIndex(block),
  };
}
